<template>
  <v-app>
    <snackbar-alert v-model="showAlert" :text="alertText" :color="alertColor" />

    <v-snackbar dense top :timeout="offlineTime" color="warning" style="max-height: 40px" :value="isOffline">
      <v-icon>warning</v-icon>
      You are offline. Data cannot be retirieved, saved, or removed.
    </v-snackbar>

    <LoadingOverlay v-model="overlay" :message="overlayMessage" />

    <v-dialog v-model="confirmDialog" persistent max-width="560" @keydown.enter.prevent="onConfirmSaveAndClose">
      <v-card class="pa-3">
        <v-card-title>Unsaved changes</v-card-title>
        <v-card-text class="black--text">{{ confirmMessage }}</v-card-text>
        <v-row class="justify-space-between px-3 pb-3">
          <g-button label="Cancel" icon="clear" :disabled="confirmSaving" @onclick="resolveConfirm('cancel')" />
          <g-button
            label="Close without saving"
            type="error"
            icon="exit_to_app"
            :disabled="confirmSaving"
            @onclick="resolveConfirm('discard')"
          />
          <g-button
            ref="saveAndCloseBtn"
            label="Save and close"
            type="primary"
            icon="save"
            :loading="confirmSaving"
            @onclick="onConfirmSaveAndClose"
          />
        </v-row>
      </v-card>
    </v-dialog>

    <TopTabs v-if="loggedIn && !loading" :class="{ docview: hidden }">
      <template>
        <MainNavigation id="mainnav" :switchEnv="storeAccount" />
        <v-main id="amain" class="full-height" fill-height>
          <router-view></router-view>
        </v-main>
      </template>
    </TopTabs>

    <section v-else-if="loading">
      <LoadingPage />
    </section>

    <section v-else>
      <LoginWrapper :loggedin="storeAccount" />
    </section>
  </v-app>
</template>

<script>
import OPTION_LIST_CONFIG from "@/config/OPTION_LIST_CONFIG";
import { LoginWrapper } from "@/modules/login";
import { LoadingPage, LoadingOverlay } from "@/modules/loading";
import { MainNavigation } from "@/modules/mainNavigation";
import { TopTabs } from "@/modules/tabs";
import { setNavigationConfirm, NAV_LEAVE_ACTION } from "@/services/navigationConfirm";

export default {
  name: "App",
  components: {
    LoginWrapper,
    LoadingPage,
    LoadingOverlay,
    MainNavigation,
    TopTabs,
  },
  data() {
    return {
      showAlert: false,
      alertText: "",
      alertColor: "",

      overlay: false,
      overlayMessage: "",

      offlineTime: -1,

      confirmDialog: false,
      confirmMessage: "",
      confirmResolver: null,
      confirmSaving: false,
    };
  },
  computed: {
    loggedIn() {
      return this.$store.getters.isLoggedIn;
    },
    loading() {
      return this.$store.getters.loading;
    },
    hidden() {
      return ["email_reply"].includes(this.$route.name);
    },
  },
  methods: {
    successHandler(msg) {
      this.showAlert = true;
      this.alertText = msg;
      this.alertColor = "success";
    },
    errorHandler(err) {
      this.setLoading(false);

      var text = this.formatErrorText(err);

      // if (!this.$store.getters["isLoggedIn"]) return;

      if (text && text.includes("jwt expired")) {
        this.$store.dispatch("redirectLogout");
        return;
      }

      this.showAlert = true;
      this.alertText = text;
      this.alertColor = "error";

      console.log(err);
    },
    formatErrorText(err) {
      if (err == null || err === "") return "Request failed";
      if (typeof err === "string") return err;
      if (typeof err === "number" || typeof err === "boolean") return String(err);

      const payload = err.data !== undefined && err.data !== null && err.data !== "" ? err.data : err;
      if (typeof payload === "string" && payload) return payload;
      if (payload && typeof payload === "object") {
        const message = payload.sqlMessage || payload.message || payload.error;
        if (typeof message === "string" && message) return message;
      }
      if (typeof err.message === "string" && err.message) return err.message;

      try {
        const serialized = JSON.stringify(payload);
        if (serialized && serialized !== "{}") return serialized;
      } catch (e) {
        /* ignore */
      }

      return "Request failed";
    },
    warnHandler(msg) {
      this.showAlert = true;
      this.alertText = msg;
      this.alertColor = "warning";
    },
    confirmHandler(message) {
      return new Promise((resolve) => {
        this.confirmMessage = message;
        this.confirmResolver = resolve;
        this.confirmSaving = false;
        this.confirmDialog = true;
        this.$nextTick(() => {
          const saveButton = this.$refs.saveAndCloseBtn?.$el;
          if (saveButton && typeof saveButton.focus === "function") {
            saveButton.focus();
          }
        });
      });
    },
    onConfirmSaveAndClose() {
      if (this.confirmSaving) return;
      this.resolveConfirm(NAV_LEAVE_ACTION.SAVE);
    },
    resolveConfirm(action) {
      if (action === NAV_LEAVE_ACTION.SAVE) {
        this.confirmSaving = true;
        this.$root.$loading?.open("Saving quote...");

        this.$store
          .dispatch("QuoteStore/saveActiveQuote")
          .then(() => {
            this.$root.$success("Quote saved");
            this.finishConfirm(NAV_LEAVE_ACTION.SAVE);
          })
          .catch((err) => {
            this.$root.$error(err);
          })
          .finally(() => {
            this.confirmSaving = false;
            this.$root.$loading?.end();
          });
        return;
      }

      this.finishConfirm(action);
    },
    finishConfirm(action) {
      this.confirmDialog = false;
      if (this.confirmResolver) {
        this.confirmResolver(action);
        this.confirmResolver = null;
      }
    },
    storeAccount(account, error) {
      if (error) {
        return this.errorHandler(error);
      }
      this.$store.dispatch("AccountStore/saveLocalAccount", account);
      this.$store.dispatch("AccountStore/getPersonalEmailList");
    },

    // LOADING VIEW
    setLoading(bool, message) {
      this.overlay = bool;
      this.overlayMessage = message;
    },

    // Handle cross-tab environment change.
    // When another tab updates `triggerEnvironmentChange`, refresh account data and reset the search state.
    handleEnvironmentChangeEvent(e) {
      if (e.key === "triggerEnvironmentChange") {
        this.$router.push({ name: "home" });
        this.$store.dispatch("AccountStore/getMe");
        this.$store.dispatch("DatabaseStore/resetSearch");
        this.$root.$success("Environment changed");
      }
    },
  },

  mounted() {
    // Get init currency
    this.$store.dispatch("CurrencyExchangeStore/fetchRates");

    // Popup config
    this.$root.$error = this.errorHandler;
    this.$root.$success = this.successHandler;
    this.$root.$warn = this.warnHandler;
    this.$root.$confirm = this.confirmHandler;
    setNavigationConfirm(this.confirmHandler.bind(this));

    var vm = this;
    this.$root.$loading = {
      open(message) {
        vm.setLoading(true, message);
      },
      update(message) {
        vm.setLoading(true, message);
      },
      end() {
        vm.setLoading(false);
      },
    };

    // Option List Stuff
    Object.keys(OPTION_LIST_CONFIG).forEach((key) => vm.$store.dispatch("ListStore/getOptionList", key));

    // Listen for cross-tab environment change events
    window.addEventListener("storage", this.handleEnvironmentChangeEvent);
  },
  beforeDestroy() {
    // Cleanup listener to avoid duplicates if component remounts
    window.removeEventListener("storage", this.handleEnvironmentChangeEvent);
  },
};
</script>

<style lang="scss">
html,
body {
  height: 100%;
}

body {
  background: #1f305c;
}

section,
.full-height {
  height: 100%;
}

/** VUE MODIFICATION **/
#app {
  height: 100%;
  background: #1f305c;
  .v-tab-bar {
    height: 35px;
  }

  #amain {
    padding-top: 30px !important;
  }

  .docview {
    #mainnav {
      display: none;
    }
    #amain {
      padding-top: 0 !important;
    }
  }

  .ring {
    border: 1px solid lightgrey;
    border-radius: 5px;
  }

  .color-alt:nth-child(even) {
    background: #f5f5f5;

    &.canclick:hover {
      background: rgba(0, 0, 0, 0.1);
    }
  }
  .color-alt:nth-child(odd) {
    background: #fff;

    &.canclick:hover {
      background: rgba(0, 0, 0, 0.1);
    }
  }

  .hover:hover {
    background: lightgray;
    cursor: pointer;
  }

  a.docdownload {
    color: black;
    text-decoration: none;
  }

  .disable-click {
    pointer-events: none;
    background: lightgrey;
    opacity: 0.8;
  }
}

/* REMOVE ARROWS IN NUMBER INPUTS */
/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type="number"] {
  -moz-appearance: textfield;
}
</style>
